/**
 * What the strategy is watching, and where each coin stands in it.
 *
 * The counters come first on purpose. Thirty rows above the words "measured
 * 482" reads as a system watching a market and showing the top of it; the same
 * thirty rows alone read as a system that watches thirty coins. The difference
 * matters to whoever is deciding whether to trust it.
 *
 * Every figure here arrived from the API. Nothing on this screen ranks, scores
 * or recomputes anything, and opening the page does not make the strategy look
 * at the market — the endpoint serves the last published window.
 */
import { useState } from "react";
import { Button } from "@/design-system/Button";

import { Panel } from "@/design-system/Panel";
import { Score } from "@/design-system/Score";
import { Empty, ErrorState, Forbidden, Loading } from "@/design-system/states/States";
import {
  StatusBadge,
  type BadgeShape,
  type BadgeTone,
} from "@/design-system/StatusBadge";
import type { RankingCandidateResponse, RankingResponse } from "@/shared/api";
import { amount, count, percent, ratio } from "@/shared/metric/metric";
import { useOverview, type OverviewState } from "@/shared/overview/useOverview";
import { availabilityOf, scoreOf } from "@/shared/strategy/score";

import { explain } from "./explain";
import { useRanking } from "./useRanking";

/**
 * One column geometry for the heads and every row.
 *
 * Written once because a header that does not sit over its column is worse
 * than no header at all: it labels the wrong figure. Columns are placed
 * explicitly rather than by source order, so the status can drop to its own
 * line on a narrow screen without the chevron following it there.
 */
const COLUMNS =
  // The score column is wider than the rail it holds: the rail is fixed at
  // 2.5rem so every row puts it in the same place, and the column is 4rem so
  // its own heading fits without pushing into the next one.
  //
  // On a wide screen the surplus goes to the status column, not the symbol.
  // Five short values across fourteen hundred pixels leave slack wherever it
  // is put; put in the first column it opened a gap between a symbol and its
  // own score, which is the one pair that has to be read together. Behind the
  // status it falls between two groups instead of inside a row.
  "grid w-full grid-cols-[2.5rem_minmax(0,1fr)_4rem_1.25rem] items-center gap-x-3 gap-y-1 px-2.5 lg:px-3" +
  " @md:grid-cols-[2.5rem_minmax(9rem,1.5fr)_minmax(4rem,0.6fr)_11rem_1.25rem]" +
  " @2xl:grid-cols-[2.5rem_12rem_4rem_minmax(11rem,1fr)_5rem_1.25rem]";

const CHEVRON_CELL = "col-start-4 row-start-1 @md:col-start-5 @2xl:col-start-6";
const STATUS_CELL = "col-span-full row-start-2 @md:col-start-4 @md:row-start-1 @md:col-span-1";
const RECOVERY_CELL = "hidden @2xl:block @2xl:col-start-5 @2xl:row-start-1";

/**
 * Form as well as colour: a status must be legible without seeing hue.
 *
 * Weight comes from the tone unless a status needs the third form. Ready and
 * active were both filled, which spent the loudest weight on the two states
 * that are simply normal — and left them differing by hue alone, which is the
 * one thing this table promises not to do. Dashed stays explicit: it means
 * "under question", and no tone implies that on its own.
 */
const STATUS: Record<string, { readonly tone: BadgeTone; readonly shape?: BadgeShape }> = {
  READY: { tone: "ready" },
  ACTIVE: { tone: "active" },
  COOLDOWN: { tone: "warning" },
  QUARANTINED: { tone: "danger" },
  QUARANTINED_REVIEW_REQUIRED: { tone: "danger", shape: "dashed" },
  BLOCKED: { tone: "muted", shape: "dashed" },
  // The guard closed the entry. Warning rather than danger: a high score with
  // the entry shut is a normal state of a normal instrument, not a fault.
  ENTRY_BLOCKED: { tone: "warning", shape: "outlined" },
};

function clock(seconds: number): string {
  const whole = Math.max(0, seconds);
  const hours = Math.floor(whole / 3600);
  const minutes = Math.floor((whole % 3600) / 60);
  return `${String(hours).padStart(2, "0")}:${String(minutes).padStart(2, "0")}`;
}

function moment(value: string): string {
  return new Date(value).toLocaleString("ru-RU", {
    hour: "2-digit",
    minute: "2-digit",
    day: "2-digit",
    month: "2-digit",
  });
}

function Counter({ label, value }: { label: string; value: number }): React.JSX.Element {
  return (
    <span className="flex items-baseline gap-1.5">
      <span className="text-label-caps text-text-secondary">{label}</span>
      <span className="text-body-main tabular-nums text-text-primary">{value}</span>
    </span>
  );
}

/**
 * What the strategy looked at, before what it chose.
 *
 * "Измерено 61" on its own reads as a strategy that watches sixty-one coins.
 * It is the last stage of a sweep over every contract the venue lists, and the
 * narrowing is deliberate: measuring costs a candle request per symbol, so it
 * is spent only on symbols liquid enough for a slot to take. Showing the
 * stages is the difference between a narrow strategy and a selective one.
 *
 * A window published before these were recorded says so by omission: the
 * stages it does not know are simply not drawn, rather than drawn as zero.
 */
function Funnel({ ranking }: { ranking: RankingResponse }): React.JSX.Element {
  const stages: { label: string; value: number; hint: string }[] = [
    ...(ranking.listed_count === null
      ? []
      : [
          {
            label: "Контрактов на бирже",
            value: ranking.listed_count,
            hint: "Bybit USDT perpetual, торгуемые сейчас",
          },
        ]),
    ...(ranking.considered_count === null
      ? []
      : [
          {
            label: "Рассматриваются",
            value: ranking.considered_count,
            hint: "Котируются с двух сторон и не в чёрном списке",
          },
        ]),
    {
      label: "Измерено",
      value: ranking.measured_count,
      hint: "Прошли фильтр ликвидности и спреда — по ним считались свечи",
    },
  ];

  return (
    <ol
      data-testid="ranking-funnel"
      className="flex flex-wrap items-center gap-x-2 gap-y-2"
    >
      {stages.map((stage, index) => (
        <li key={stage.label} className="flex items-center gap-2">
          {index > 0 && (
            <span aria-hidden="true" className="text-text-muted">
              <svg
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="1.5"
                strokeLinecap="round"
                strokeLinejoin="round"
                className="h-3.5 w-3.5"
              >
                <path d="M5 12h14M13 6l6 6-6 6" />
              </svg>
            </span>
          )}
          <span className="flex items-baseline gap-1.5" title={stage.hint}>
            <span className="text-label-caps text-text-secondary">{stage.label}</span>
            <span className="text-body-main tabular-nums text-text-primary">{stage.value}</span>
          </span>
        </li>
      ))}
    </ol>
  );
}

function Status({ candidate }: { candidate: RankingCandidateResponse }): React.JSX.Element {
  // The guard has the last word on whether a slot could take this now. The
  // preview carries no room for a second badge beside the status, so a row
  // showing "Готова" while the entry was shut said the opposite of the truth
  // with nothing next to it to correct the impression.
  const regime = scoreOf(candidate).regime;
  const available = availabilityOf(
    candidate.status,
    regime?.verdict ?? null,
    regime?.reason,
  );
  const look = STATUS[available.status] ?? { tone: "muted" as BadgeTone, shape: "dashed" as BadgeShape };
  return (
    // Two fixed parts: the badge, then whatever qualifies it. Both start at
    // the same x on every row, so a column of states reads as a column rather
    // than as a ragged edge — the badge widths differ by word length, which is
    // not information.
    <span className="flex min-w-0 items-center gap-2">
      <span className="flex w-[5.5rem] shrink-0 justify-start">
        <StatusBadge
          tone={look.tone}
          shape={look.shape}
          testId={`status-${candidate.symbol}`}
        >
          {available.label}
        </StatusBadge>
      </span>
      <span className="min-w-0 truncate font-mono text-helper-sm tabular-nums text-text-secondary">
        {available.reason ??
          candidate.active_slot_id ??
          (candidate.cooldown_remaining_seconds === null
            ? ""
            : clock(candidate.cooldown_remaining_seconds))}
      </span>
      {candidate.review_required && (
        <span className="shrink-0 text-helper-sm text-status-negative">Проверка</span>
      )}
    </span>
  );
}

function Detail({ label, value }: { label: string; value: string }): React.JSX.Element {
  return (
    <div className="flex min-w-0 items-start justify-between gap-4 border-b border-border-subtle py-1 last:border-b-0">
      <dt className="min-w-0 text-text-secondary">{label}</dt>
      <dd className="min-w-0 max-w-1/2 shrink-0 break-all text-right font-mono tabular-nums text-text-primary">
        {value}
      </dd>
    </div>
  );
}

function Details({
  candidate,
  id,
}: {
  candidate: RankingCandidateResponse;
  id: string;
}): React.JSX.Element {
  const current = candidate.logical_pool !== null && candidate.logical_pool !== undefined;
  if (current) {
    return (
      <div id={id} className="flex flex-col gap-3 bg-surface-hover px-4 py-3 text-sm">
        <p className="max-w-prose text-text-secondary">
          Score v2 описывает пригодность отдельно для логического пула; Entry Guard
          остаётся независимым veto.
        </p>
        <dl className="grid grid-cols-1 gap-x-8 @3xl:grid-cols-2">
          <Detail label="Логический пул" value={candidate.logical_pool ?? "—"} />
          <Detail label="Итоговая оценка" value={candidate.final_score ?? "—"} />
          <Detail label="Политика оценки" value={candidate.score_policy_version ?? "UNKNOWN"} />
          <Detail label="Версия фильтра входа" value={candidate.entry_guard?.version ?? "UNKNOWN"} />
          {candidate.flash_profile != null && <>
            <Detail label="Профиль" value={candidate.flash_profile.version} />
            <Detail label="Хэш профиля" value={candidate.flash_profile.content_sha256} />
          </>}
          <Detail label="Доказательность" value={candidate.evidence_level ?? "—"} />
          <Detail label="Версия Score" value={candidate.score_version} />
          <Detail
            label="Фильтр входа"
            value={candidate.entry_guard?.verdict ?? "UNKNOWN"}
          />
          <Detail
            label="Код фильтра входа"
            value={candidate.entry_guard?.reason_code ?? "—"}
          />
          <Detail
            label="Отсечка свечи фильтра"
            value={
              candidate.entry_guard?.candle_cutoff === null ||
              candidate.entry_guard?.candle_cutoff === undefined
                ? "—"
                : moment(candidate.entry_guard.candle_cutoff)
            }
          />
          <Detail label="Причина выбора" value={candidate.reason_code ?? "—"} />
          <Detail label="Уже в работе" value={candidate.already_held ? "да" : "нет"} />
          {(candidate.contributions ?? []).map((item) => (
            <Detail
              key={item.feature}
              label={`Contribution · ${item.feature}`}
              value={`raw ${item.raw_value ?? "UNKNOWN"} · normalized ${item.normalized_value ?? "UNKNOWN"} · contribution ${item.contribution} · weight ${item.applied_weight}`}
            />
          ))}
        </dl>
      </div>
    );
  }
  return (
    <div id={id} className="flex flex-col gap-3 bg-surface-hover px-4 py-3 text-sm">
      <p className="max-w-prose text-text-secondary">{explain(candidate)}</p>
      {/* Every figure is shown at the precision a person reads it at. The exact
          value the server measured is what the strategy acted on and is not
          lost -- it is simply not what a row is for. */}
      <dl className="grid grid-cols-1 gap-x-8 @3xl:grid-cols-2">
        <Detail label="Доля восстановлений" value={percent(candidate.recovery_rate ?? null)} />
        <Detail label="Частота откатов" value={percent(candidate.pullback_rate ?? null)} />
        <Detail
          label="Односторонность падения"
          value={percent(candidate.decline_persistence ?? null)}
        />
        <Detail label="Спред" value={percent(candidate.spread ?? null, 3)} />
        <Detail label="Оборот за сутки, USDT" value={amount(candidate.turnover ?? null)} />
        <Detail
          label="Медиана восстановления, баров"
          value={count(candidate.median_recovery_bars ?? null)}
        />
        <Detail label="Рыночная оценка" value={ratio(candidate.market_score ?? null)} />
        <Detail label="Оценка по обороту" value={ratio(candidate.turnover_score ?? null)} />
        <Detail
          label="Завершённых циклов"
          value={count(candidate.completed_cycle_count ?? null)}
        />
        <Detail label="Режим оценки" value={candidate.ranking_mode ?? "—"} />
        <Detail label="Версия оценки" value={candidate.score_version} />
        <Detail label="Код причины" value={candidate.reason_code ?? "—"} />
      </dl>
    </div>
  );
}

/**
 * The one affordance a row has: something here opens.
 *
 * An SVG rather than a character, so it inherits the text colour in both
 * themes and scales with the type. Hidden from the accessibility tree because
 * `aria-expanded` on the button already says everything it says, and saying it
 * twice is how a screen reader starts repeating itself.
 */
function Chevron({ open }: { open: boolean }): React.JSX.Element {
  return (
    <svg
      aria-hidden="true"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
      className={`h-4 w-4 text-text-muted transition-transform duration-200 ease-out motion-reduce:transition-none ${
        open ? "rotate-180" : ""
      }`}
    >
      <path d="m6 9 6 6 6-6" />
    </svg>
  );
}

function ColumnHeads(): React.JSX.Element {
  return (
    <div
      className={`${COLUMNS} border-b border-border-subtle py-2 text-xs tracking-wide text-text-secondary`}
      data-testid="ranking-heads"
      aria-hidden="true"
    >
      <span className="col-start-1 row-start-1">#</span>
      <span className="col-start-2 row-start-1">Инструмент</span>
      <span className="col-start-3 row-start-1 truncate text-right">Оценка</span>
      <span className={`${STATUS_CELL} hidden truncate @md:block`}>Состояние</span>
      {/* Shortened, with the full word on hover. A heading clipped to
          "Восстановле…" is a heading nobody can read, and widening the column
          to fit it would take the space from the instrument name. */}
      <span
        className={`${RECOVERY_CELL} truncate text-right`}
        title="Доля откатов, после которых цена восстановилась"
      >
        Восст. %
      </span>
    </div>
  );
}

function Row({ candidate }: { candidate: RankingCandidateResponse }): React.JSX.Element {
  const [open, setOpen] = useState(false);
  const panelId = `ranking-detail-${candidate.symbol}-${candidate.logical_pool ?? "legacy"}`;

  return (
    <li className="border-b border-border-subtle last:border-b-0">
      <button
        type="button"
        aria-expanded={open}
        aria-controls={panelId}
        onClick={() => {
          setOpen((was) => !was);
        }}
        className={`${COLUMNS} min-h-11 cursor-pointer py-2 text-left transition-colors duration-150 hover:bg-surface-hover focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-focus-ring`}
      >
        <span className="col-start-1 row-start-1 font-mono text-sm tabular-nums text-text-secondary">
          {candidate.rank === null ? "—" : `#${String(candidate.rank)}`}
        </span>
        <span className="col-start-2 row-start-1 min-w-0 truncate font-medium text-text-primary">
          {candidate.symbol}
        </span>
        <span className="col-start-3 row-start-1 flex justify-end">
          {/* The rank has its own column at the head of the row. Repeating it
              inside the score says the same thing twice and makes the reader
              check whether the two agree. */}
          {candidate.final_score === null || candidate.final_score === undefined ? (
            <Score
              value={candidate.skryptos_score ?? 0}
              testId={`score-${candidate.symbol}`}
            />
          ) : (
            <span className="font-mono text-sm tabular-nums text-text-primary">
              {candidate.final_score}
            </span>
          )}
        </span>
        {/* One node, not two. On a narrow screen it drops to its own line
            rather than being duplicated: a second copy would read twice to a
            screen reader and drift from the first the moment either changes. */}
        <span className={`${STATUS_CELL} min-w-0`}>
          <Status candidate={candidate} />
        </span>
        {/* Recovery is the one deep figure worth carrying in the row: it is the
            behaviour the whole strategy depends on. Dropped first when space
            runs out, because the row below it opens with every figure in it. */}
        <span
          data-testid={`recovery-${candidate.symbol}`}
          className={`${RECOVERY_CELL} text-right font-mono text-xs tabular-nums text-text-secondary`}
        >
          {candidate.logical_pool ?? percent(candidate.recovery_rate ?? null)}
        </span>
        <span className={`${CHEVRON_CELL} flex justify-end`}>
          <Chevron open={open} />
        </span>
      </button>
      {open && <Details candidate={candidate} id={panelId} />}
    </li>
  );
}

function Header({ ranking }: { ranking: RankingResponse }): React.JSX.Element {
  return (
    <div className="flex flex-col gap-2 border-b border-border-subtle px-3 py-2.5 lg:px-4">
      {ranking.state === "STALE" && (
        <p
          data-testid="ranking-stale"
          className="rounded-row border border-status-warning px-2 py-1 text-helper-sm text-status-warning"
        >
          Данные устарели. Рейтинг обновляется каждые 15 минут — последний расчёт
          не пришёл вовремя.
        </p>
      )}

      {/* The sweep and the selection on one line each, and both on one line
          together once there is room. Two grids of six numbers made a screen
          where the field the strategy looked at and the field it will take
          from read as stages of one thing. */}
      <div className="flex flex-wrap items-center gap-x-5 gap-y-2">
        <Funnel ranking={ranking} />
        <span aria-hidden="true" className="hidden h-6 w-px bg-border-subtle lg:block" />
        <div className="flex flex-wrap items-center gap-x-4 gap-y-2" data-testid="ranking-counters">
          <Counter label="Допущено" value={ranking.eligible_count} />
          <Counter label="Готовы" value={ranking.ready_count} />
          <Counter label="В работе" value={ranking.active_count} />
          <Counter label="Отдыхают" value={ranking.cooldown_count} />
          <Counter label="Карантин" value={ranking.quarantined_count} />
        </div>
      </div>

      <p className="text-helper-sm text-text-muted" data-testid="ranking-window">
        {`Показано ${String(ranking.shown_count)} · ещё ${String(ranking.below_the_fold)} в рейтинге`}
      </p>
      {ranking.strategy_version === "base-v2.0" && ranking.market_data_cutoff != null && (
        <p className="break-all font-mono text-helper-sm text-text-muted" data-testid="ranking-identity">
          {`BASE v2 · cutoff ${moment(ranking.market_data_cutoff)} · snapshot ${ranking.market_snapshot_sha256 ?? "UNKNOWN"}`}
        </p>
      )}
    </div>
  );
}

/** What stands in the column by default: enough to see where the next few
 *  slots would go, short enough to scan beside the account. */
const SHORTLIST = 8;

/**
 * One row per instrument, at the best score it holds.
 *
 * BASE v2 scores an instrument once per pool and publishes the rows pool by
 * pool: every PRIME row, then every PULSE row. A preview taking the first
 * eight therefore took the eight best PRIME rows and PULSE never appeared on
 * Обзор at all — on the live pass, a hundred and eleven instruments were
 * ranked in PULSE and a reader of the summary screen could not see one of
 * them.
 *
 * Deduplicated rather than interleaved, because a slot goes to an instrument
 * and not to an instrument-in-a-pool: eight rows showing four contracts twice
 * is a preview of four. The pool it scored best in travels with the row and is
 * shown there, so nothing is implied about which pool a cycle would open in —
 * that is decided by the quota when a slot frees, and the full screen says so.
 */
function instruments(
  candidates: readonly RankingCandidateResponse[],
): readonly RankingCandidateResponse[] {
  const best = new Map<string, RankingCandidateResponse>();
  for (const one of candidates) {
    const held = best.get(one.symbol);
    if (held === undefined || rank(one) < rank(held)) best.set(one.symbol, one);
  }
  // By the rank each instrument earned in the pool it does best in. Ordered
  // here rather than trusting the publication order, which is by pool first.
  return [...best.values()].sort((left, right) => rank(left) - rank(right));
}

/** Where a row stands in its own pool. Unranked sorts last, never as first. */
function rank(one: RankingCandidateResponse): number {
  return one.rank ?? Number.MAX_SAFE_INTEGER;
}

/**
 * Whether a slot could actually be funded right now.
 *
 * `undefined` where the capital record has no high-water mark to read a
 * schedule from — not zero. A ranking collapsed because a figure was missing
 * would be a screen closing a list for no stated reason.
 */
function fundable(overview: OverviewState): number | undefined {
  if (overview.status !== "ready") return undefined;
  return overview.overview.performance.capital?.fundable_free_slots;
}

/**
 * The ranking when nothing can be entered, in one line.
 *
 * The list is a queue for slots, and with no slot to fund it is a queue for
 * nothing — a funnel, five counters and eight rows taking a full region of
 * Обзор to describe a decision the strategy cannot act on today. Collapsed
 * rather than removed: the leader is still worth a glance, the reason the list
 * is closed is stated where the list was, and one click opens it in place.
 */
function Idle({
  ranking,
  onOpen,
}: {
  readonly ranking: RankingResponse;
  readonly onOpen: () => void;
}): React.JSX.Element {
  const leader = ranking.top_candidates[0];
  return (
    <div
      data-testid="ranking-idle"
      className="flex flex-wrap items-center justify-between gap-x-4 gap-y-2 px-3 py-2.5 lg:px-4"
    >
      <p className="text-helper-sm text-text-muted">
        {`Свободных слотов нет — новые циклы сейчас не открываются.${
          leader === undefined
            ? ""
            : ` Лидер рейтинга — ${leader.symbol} · ${
                leader.final_score ?? String(leader.skryptos_score ?? "—")
              }.`
        }`}
      </p>
      <Button variant="ghost" size="sm" data-testid="open-idle-ranking" onClick={onOpen}>
        Показать рейтинг
      </Button>
    </div>
  );
}

export function RankingPanel(): React.JSX.Element {
  const [full, setFull] = useState(false);
  // Opened by hand while the strategy has nothing to enter. Separate from
  // `full`, which is TOP-8 against TOP-30: these are two different questions,
  // and one flag answering both would open thirty rows on a single click.
  const [opened, setOpened] = useState(false);
  const state = useRanking();
  // The same query key the page already used, so this is the cached reading
  // rather than a second request for one number.
  const free = fundable(useOverview());

  if (state.status === "loading") return <Loading title="Считываю рейтинг…" />;
  if (state.status === "forbidden") return <Forbidden />;
  if (state.status === "unreadable") {
    return (
      <ErrorState
        title="Не могу прочитать рейтинг"
        detail="Данные не пришли. Позиции и ордера на бирже от этого экрана не зависят."
      />
    );
  }

  const { ranking } = state;

  if (ranking.state === "NO_DATA_YET") {
    return (
      <Panel
        title="Skryptos Score — TOP 30"
        labelledBy="ranking-heading"
        testId="ranking-panel"
        flush
      >
        <Empty
          title="Рейтинг ещё не рассчитан"
          detail={`Он появится после первого рыночного прохода ${ranking.strategy_version ?? "активной стратегии"} — в течение 15 минут.`}
        />
      </Panel>
    );
  }

  // Collapsed while nothing can be funded, and returned early rather than
  // hidden inside the block: the header belongs to the list. The funnel counts
  // how many instruments survived to a queue that is closed, and a reader
  // shown "допущено 74" over one line saying no slot is free has been given
  // the shape of a full region with none of its use.
  if (free === 0 && !opened) {
    return (
      <Panel
        title={`Рейтинг Skryptos · ${ranking.strategy_version ?? "LIVE"}`}
        labelledBy="ranking-heading"
        testId="ranking-panel"
        flush
      >
        <Idle
          ranking={ranking}
          onOpen={() => {
            setOpened(true);
          }}
        />
      </Panel>
    );
  }

  const queue = instruments(ranking.top_candidates);
  const shown = full ? queue : queue.slice(0, SHORTLIST);
  const hidden = queue.length - shown.length;

  return (
    <Panel
      title="Рейтинг Skryptos"
      labelledBy="ranking-heading"
      testId="ranking-panel"
      flush
      trailing={
        // Only when this window is past its own budget. The status strip
        // carries the market feed's age at all times, so a panel repeating it
        // adds a clock and no information; a panel whose figures have gone
        // stale is the case the strip cannot make locally visible.
        ranking.generated_at === null || ranking.state !== "STALE" ? undefined : (
          <span
            className="text-helper-sm text-status-warning"
            data-testid="ranking-generated"
            data-stale="true"
          >
            {`устарел · ${moment(ranking.generated_at)}`}
          </span>
        )
      }
    >
      {/* The container the columns are measured against. This list lives in a
          narrow column beside the account and, when the whole ranking is open,
          in the same narrow column — so the window says nothing useful about
          how much room it has. */}
      <div className="@container">
        <Header ranking={ranking} />
        <div className={full ? "max-h-[60vh] overflow-y-auto" : ""}>
          <ColumnHeads />
        {/* Open, it scrolls inside itself rather than pushing everything below
            it down a page and a half. The list grows where it stands; the page
            does not move under the reader who clicked. */}
          <ul aria-label="Кандидаты рейтинга">
            {shown.map((candidate) => (
              <Row
                key={`${candidate.symbol}-${candidate.logical_pool ?? "legacy"}`}
                candidate={candidate}
              />
            ))}
          </ul>
        </div>

        {/* What the numbers in the score column are, said once under them. A
            column that runs 98, 90, 49 invites the reader to look for the
            missing ranks between them; the scale is a fit, not a queue. */}
        <p
          className="border-t border-border-subtle px-2.5 py-1.5 text-helper-sm text-text-muted"
          data-testid="ranking-scale"
          title="Разрыв между соседними оценками означает, что следующий кандидат подходит заметно хуже, а не что между ними кого-то пропустили. Ранг — порядок в этом же списке."
        >
          {ranking.strategy_version === "base-v2.0"
            ? "Score и Guard каждого пула показаны из backend facts."
            : "Оценка 0–100 — пригодность для оборота BASE v1, не очередь."}
        </p>

        {/* The rest of the list opens here, under the last row, rather than as
            a second block somewhere else on the page. A list that grows is one
            list; a list that summons a copy of itself elsewhere is two. */}
        {(hidden > 0 || full) && (
        <Button
          variant="ghost"
          size="sm"
          aria-expanded={full}
          data-testid="toggle-full-ranking"
          onClick={() => {
            setFull((was) => !was);
          }}
          className="min-h-9 w-full gap-1 rounded-none border-t border-border-subtle text-action-primary hover:text-action-hover pointer-coarse:min-h-11"
        >
          {/* Not "TOP-30": the window carries every ranked instrument — a
              hundred and eleven of them on the live pass — and a button
              promising thirty was describing a limit that no longer exists. */}
          {full ? "Свернуть" : `Показать весь рейтинг · ещё ${String(hidden)}`}
        </Button>
        )}
      </div>
    </Panel>
  );
}
